import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { createTag } from '../../graphql/mutation'
import {
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Snackbar,
  Paper,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import SaveIcon from '@mui/icons-material/Save'

export default function CreateTagPage() {
  const navigate = useNavigate()

  const [tag, setTag] = useState({
    name: '',
    isDefault: false
  })

  const [progress, setProgress] = useState(false)
  const [snackBarOpen, setSnackBarOpen] = useState(false)
  const [snackBarMessage, setSnackBarMessage] = useState(null)
  const [snackBarSeverity, setSnackBarSeverity] = useState('success')

  const handleChange = (e) => {
    const { name, value } = e.target
    setTag((prev) => ({ ...prev, [name]: value }))
  }

  const handleToggleChange = (event, newValue) => {
    if (newValue !== null) {
      setTag((prev) => ({ ...prev, isDefault: newValue }))
    }
  }

  const [createTagMutation] = useMutation(createTag)

  const handleCreateTag = async () => {
    if (!tag.name.trim()) {
      setSnackBarMessage('Please enter a tag name')
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
      return
    }

    setProgress(true)
    try {
      const response = await createTagMutation({
        variables: {
          name: tag.name.trim(),
          isDefault: tag.isDefault
        },
      })

      if (response.data) {
        setSnackBarMessage('Tag created successfully!')
        setSnackBarSeverity('success')
        setSnackBarOpen(true)
        
        // Navigate to the new tag's detail page
        setTimeout(() => {
          navigate(`/tags/${response.data.createTag.id}`)
        }, 1500)
      }
    } catch (error) {
      console.error('Error creating tag:', error)
      setSnackBarMessage(`Error creating tag: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setProgress(false)
    }
  }

  const handleSnackBarClose = () => {
    setSnackBarOpen(false)
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 4, 
      maxWidth: '600px', 
      mx: 'auto', 
      px: 2, 
      py: 2 
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <LocalOfferIcon fontSize="large" sx={{ color: '#1976d2' }} />
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
          Create New Tag
        </Typography>
      </Box>

      {/* Main Content */}
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Tag Name */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Tag Name *
            </Typography>
            <TextField
              fullWidth
              name="name"
              value={tag.name}
              onChange={handleChange}
              placeholder="Enter tag name..."
              variant="outlined"
              required
              sx={{ mb: 1 }}
            />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Enter a descriptive name for your tag
            </Typography>
          </Box>

          {/* Is Default */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Is Default Tag
            </Typography>
            <ToggleButtonGroup
              value={tag.isDefault}
              exclusive
              onChange={handleToggleChange}
              aria-label="default tag toggle"
              fullWidth
              sx={{ mb: 1 }}
            >
              <ToggleButton 
                value={false} 
                aria-label="not default"
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#bbdefb'
                    }
                  }
                }}
              >
                No
              </ToggleButton>
              <ToggleButton 
                value={true} 
                aria-label="default"
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: '#e8f5e8',
                    color: '#2e7d32',
                    '&:hover': {
                      backgroundColor: '#c8e6c9'
                    }
                  }
                }}
              >
                Yes
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Toggle whether this tag should be a default tag
            </Typography>
          </Box>
          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/tags')}
              sx={{ minWidth: 100 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={progress ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleCreateTag}
              disabled={progress || !tag.name.trim()}
              sx={{ 
                minWidth: 120,
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0'
                }
              }}
            >
              {progress ? 'Creating...' : 'Create Tag'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackBarOpen}
        autoHideDuration={4000}
        onClose={handleSnackBarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackBarClose} 
          severity={snackBarSeverity}
          sx={{ width: '100%' }}
        >
          {snackBarMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
} 